import { useState, useEffect } from "react";
import { notify } from "@/components/ui/notification";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createProjectsApi } from "@/lib/api/projects";
import type { Project } from "@/lib/api/projects";

interface RenameProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  currentName: string;
  getToken: () => Promise<string | null>;
  onSuccess?: (newName: string) => void;
}

export function RenameProjectDialog({
  open,
  onOpenChange,
  projectId,
  currentName,
  getToken,
  onSuccess,
}: RenameProjectDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(currentName);

  useEffect(() => {
    if (open) setName(currentName);
  }, [open, currentName]);

  const projectsApi = createProjectsApi({ getToken });

  const mutation = useMutation({
    mutationFn: (newName: string) => projectsApi.update(projectId, { name: newName }),
    onSuccess: (updated) => {
      queryClient.setQueryData<Project>(["project", projectId], updated);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      onOpenChange(false);
      onSuccess?.(updated.name);
    },
    onError: () => {
      notify.error("Could not rename project", { description: "The name was not saved. Try again." });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-surface-raised border-white/10 p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-surface-raised-text">
            Rename project
          </DialogTitle>
          <DialogDescription className="text-surface-raised-text-muted">
            Update how this project appears in your workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label
              htmlFor="rename-project-name"
              className="text-sm font-medium text-surface-raised-text"
            >
              Display name
            </label>
            <Input
              id="rename-project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && name.trim() && mutation.mutate(name)
              }
              placeholder="Clean Canvas"
              className="bg-surface-raised-hover border-white/10 text-surface-raised-text"
            />
            <p className="text-xs text-surface-raised-text-muted">
              Supports spaces and special characters, up to 100 characters.
              Note: this name is only visible to you and members of your
              workspace, not to visitors of your published app.
            </p>
          </div>
        </div>
        <DialogFooter className="bg-transparent border-t-0 -mx-6 -mb-6 p-6 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
            className="bg-transparent border-white/20 text-surface-raised-text hover:bg-surface-raised-hover"
          >
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate(name)}
            disabled={mutation.isPending || !name.trim()}
            className="bg-white text-black hover:bg-white/90"
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
